// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable, OnDestroy } from '@angular/core';

import { CtsGantryTimeZone } from '../interfaces/cts-gantry-time-zone';
import { BehaviorSubject, Observable } from 'rxjs';
import timeZones from '../../../../assets/timezone/time-zone-data.json';

@Injectable({ providedIn: 'root' })
export class CtsGantryTimeZoneService implements OnDestroy {
  public readonly TIMEZONE_KEY = '@ctStatusTimeZone';

  private readonly timeZones$ = new BehaviorSubject<CtsGantryTimeZone[]>(
    timeZones,
  );

  private readonly timeZone$ = new BehaviorSubject<
    CtsGantryTimeZone | undefined
  >(this.retrieve());

  private readonly broadcastChannel: BroadcastChannel;

  public constructor() {
    this.broadcastChannel = new BroadcastChannel('ct-status-timezone-channel');
    this.broadcastChannel.onmessage = (event) => {
      this.timeZone$.next(event.data);
    };
  }

  public ngOnDestroy(): void {
    this.broadcastChannel.close();
  }

  public timeZones(): Observable<CtsGantryTimeZone[]> {
    return this.timeZones$.asObservable();
  }

  public timeZone(): Observable<CtsGantryTimeZone | undefined> {
    return this.timeZone$.asObservable();
  }

  public store(tz: CtsGantryTimeZone): void {
    if (tz) {
      localStorage.setItem(this.TIMEZONE_KEY, JSON.stringify(tz));
      this.timeZone$.next(tz);
      this.broadcastChannel.postMessage(tz);
    }
  }

  private retrieve(): CtsGantryTimeZone | undefined {
    const tz = localStorage.getItem(this.TIMEZONE_KEY);
    return tz ? (JSON.parse(tz) as CtsGantryTimeZone) : undefined;
  }

  public remove(): void {
    localStorage.removeItem(this.TIMEZONE_KEY);
    this.timeZone$.next(undefined);
    this.broadcastChannel.postMessage(undefined);
  }

  public secondsToGMTString(offset: number | undefined): string {
    if (offset) {
      const remainInSeconds = Math.abs(offset) % 3600;
      const hours = (Math.abs(offset) - remainInSeconds) / 3600;
      const minutes = remainInSeconds / 60;

      const sHours = String(hours).length < 2 ? '0' + hours : '' + hours;
      const sMinutes =
        String(minutes).length < 2 ? '0' + minutes : '' + minutes;

      return `(GMT${offset < 0 ? '-' : '+'}${sHours}:${sMinutes})`;
    }

    return '( - )';
  }
}
